import { uuidv4 } from "./uuid";
import type { VisitorInfo } from "../types";

const VISITOR_ID_KEY = "nejix_chat_visitor_id";
const VISITOR_INFO_KEY = "nejix_chat_visitor_info";

export interface StoredSession {
  session_id: string;
  token: string;
  ws_url: string;
  expires_at: number; // epoch ms
}

function sessionKey(siteKey: string): string {
  return `nejix_chat_session_${siteKey}`;
}

// Visitor id is stable across visits. Used as a soft identifier so
// the same browser is recognizable across sessions for analytics; not
// a security boundary.
export function getOrCreateVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode, blocked, etc.) — use an
    // ephemeral id for this page load.
    return uuidv4();
  }
}

export function saveVisitorInfo(info: VisitorInfo): void {
  try {
    localStorage.setItem(VISITOR_INFO_KEY, JSON.stringify(info));
  } catch {
    // ignore
  }
}

export function loadVisitorInfo(): VisitorInfo | null {
  try {
    const raw = localStorage.getItem(VISITOR_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VisitorInfo;
    if (!parsed.name || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(siteKey: string, data: StoredSession): void {
  try {
    localStorage.setItem(sessionKey(siteKey), JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function loadSession(siteKey: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(siteKey));
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredSession;
    if (!data?.session_id || !data?.token || !data?.ws_url) return null;
    if (typeof data.expires_at === "number" && data.expires_at < Date.now()) {
      clearSession(siteKey);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function clearSession(siteKey: string): void {
  try {
    localStorage.removeItem(sessionKey(siteKey));
  } catch {
    // ignore
  }
}

function nudgeKey(siteKey: string): string {
  return `nejix_chat_nudge_${siteKey}`;
}

// The launcher attention nudge fires at most once per browser session.
// sessionStorage (not localStorage) gives "once per visit" semantics —
// a returning visitor in a fresh tab sees it again, but reloads and
// SPA navigations within the same tab don't re-nag.
export function wasNudgeShown(siteKey: string): boolean {
  try {
    return sessionStorage.getItem(nudgeKey(siteKey)) === "1";
  } catch {
    // sessionStorage unavailable — treat as already shown so a blocked
    // visitor never gets a wiggle that can't be remembered/suppressed.
    return true;
  }
}

export function markNudgeShown(siteKey: string): void {
  try {
    sessionStorage.setItem(nudgeKey(siteKey), "1");
  } catch {
    // ignore
  }
}
